import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../services/api';
import { btn, c, chart, tbl } from '../../theme';
import { fmtCount, fmtHm, fmtClock, fmtPct, STATUS, statusColor } from './format';
import { transferPct } from './AgentTable';

// One agent over the page's window, in a panel over the right of the page:
// what makes up their Handled, handle time and hold, each channel, and the
// same day by day. Read from stored data, like the table, so the panel and the
// row it was opened from always agree. (The old panel queried Genesys live on
// every open, so its Today could differ from the row's.)

const dayLabel = (ymd) => {
  const [, m, d] = ymd.split('-').map(Number);
  return `${m}/${d}`;
};

export default function AgentDetail({ userId, range, dept, onClose }) {
  const closeRef = useRef(null);
  const q = useQuery({
    queryKey: ['cc-agent', userId, dept, range.start, range.end],
    queryFn: () => api.get(`/contact-center/agents/${userId}`, { params: { start: range.start, end: range.end, dept } }),
  });
  const a = q.data?.agent;

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const facts = a ? [
    ['Handled', fmtCount(a.handled)],
    ['Inbound calls', fmtCount(a.answered)],
    ['Callbacks connected', fmtCount(a.outbound)],
    ['Email', fmtCount(a.emailHandled)],
    ...(a.noConnects > 0 ? [['Callbacks not connected', fmtCount(a.noConnects)]] : []),
    ['AHT', fmtClock(a.aht)],
    ['Average hold', fmtClock(a.avgHold)],
    ['Transfers', `${fmtCount(a.transferred)}${transferPct(a) != null ? ` · ${fmtPct(transferPct(a))}` : ''}`],
    ['Adherence', a.adherenceDays ? `${fmtPct(a.adherencePct)} · ${a.adherenceDays} day${a.adherenceDays === 1 ? '' : 's'}` : '—'],
    ['QA', a.qaCount ? `${a.qaAvg.toFixed(1)} · ${a.qaCount} evaluation${a.qaCount === 1 ? '' : 's'}` : '—'],
  ] : [];

  return (
    <>
      <div style={s.scrim} onClick={onClose} aria-hidden="true" />
      <aside style={s.panel} role="dialog" aria-modal="true" aria-label={a?.name ? `Agent ${a.name}` : 'Agent'}>
        <div style={s.head}>
          <div>
            <div style={s.eyebrow}>{range.label}</div>
            <h2 style={s.h2}>{a ? (a.name || 'Unknown agent') : 'Agent'}</h2>
            {a && <div style={s.sub}>{a.pod}{a.state && a.state !== 'active' ? ' · no longer active in Genesys' : ''}</div>}
          </div>
          <button ref={closeRef} type="button" style={btn.secondaryXs} onClick={onClose}>Close</button>
        </div>

        {q.isLoading && <p style={s.muted}>Loading…</p>}
        {q.error && <p style={s.error}>Could not load this agent: {q.error.message}</p>}

        {a && (
          <>
            <dl style={s.facts}>
              {facts.map(([k, v]) => (
                <div key={k} style={s.fact}><dt style={s.dt}>{k}</dt><dd style={s.dd}>{v}</dd></div>
              ))}
            </dl>
            <p style={s.note}>
              Handled counts each conversation once: inbound calls, callbacks that connected, and email.
              A connected callback is two legs in Genesys and one here.
              {a.stuckExcludedSecs > 0 && ` ${fmtClock(a.stuckExcludedSecs)} on legs Genesys left open was left out of handle time.`}
            </p>

            {a.time && <TimeBreakdown t={a.time} handledPerHour={a.handledPerHour} />}

            <h3 style={s.h3}>By channel</h3>
            <table style={s.table}>
              <thead>
                <tr><th style={s.th}>Channel</th><th style={s.thNum}>Legs handled</th><th style={s.thNum}>AHT</th></tr>
              </thead>
              <tbody>
                {a.channels.map((ch) => (
                  <tr key={ch.channel}>
                    <td style={s.td}>{ch.channel}</td>
                    <td style={s.num}>{fmtCount(ch.handled)}</td>
                    <td style={s.num}>{fmtClock(ch.aht)}</td>
                  </tr>
                ))}
                {!a.channels.length && <tr><td colSpan={3} style={s.td}>No work on a Support queue.</td></tr>}
              </tbody>
            </table>
            <p style={s.note}>Channels count legs on each queue, so they can add up to more than Handled.</p>

            {a.daily.length > 1 && (
              <>
                <h3 style={s.h3}>Day by day</h3>
                <div style={{ height: 200 }} role="img"
                  aria-label={a.daily.map((d) => `${dayLabel(d.day)}: ${d.handled} handled`).join(', ')}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={a.daily.map((d) => ({ ...d, label: dayLabel(d.day) }))} margin={{ top: 6, right: 4, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke={c.canvas} vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: c.textMuted }} interval="preserveStartEnd" />
                      <YAxis yAxisId="n" tick={{ fontSize: 10, fill: c.textMuted }} allowDecimals={false} />
                      <YAxis yAxisId="t" orientation="right" tick={{ fontSize: 10, fill: c.textMuted }} tickFormatter={fmtClock} width={40} />
                      <Tooltip formatter={(v, name) => (name === 'AHT' ? fmtClock(v) : fmtCount(v))} />
                      <Bar yAxisId="n" dataKey="handled" name="Handled" fill={chart.categorical[0]} isAnimationActive={false} />
                      <Line yAxisId="t" dataKey="aht" name="AHT" stroke={c.navy} dot={false} strokeWidth={1.5} connectNulls={false} isAnimationActive={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <p style={s.note}>Bars: Handled. Line: AHT (right scale).</p>
              </>
            )}
          </>
        )}
      </aside>
    </>
  );
}

const WORKING_LABELS = {
  onQueue: 'On Queue, less hold', training: 'Training', coaching: 'Coaching',
  outbound: 'Outbound Interaction', meeting: 'Meeting', supervisorApproved: 'Supervisor Approved',
};

// Where the logged-in time went: what counted as work, what was taken out of
// the denominator (break, lunch), and the rest, every code by name. Rows of
// zero are left out; the totals are the record's own.
function TimeBreakdown({ t, handledPerHour }) {
  const rows = (obj, labels = {}) => Object.entries(obj).filter(([, v]) => v > 0)
    .map(([k, v]) => <tr key={k}><td style={s.td}>{labels[k] || k}</td><td style={s.num}>{fmtHm(v)}</td></tr>);
  const tone = STATUS.efficiency(t.efficiency);
  return (
    <>
      <h3 style={s.h3}>Time</h3>
      <dl style={s.facts}>
        <div style={s.fact}>
          <dt style={s.dt}>Efficiency</dt>
          <dd style={{ ...s.dd, color: statusColor(tone) }}>{t.efficiency == null && t.unnamedSecs > 0 ? 'incomplete' : fmtPct(t.efficiency)}</dd>
        </div>
        <div style={s.fact}><dt style={s.dt}>Occupancy</dt><dd style={s.dd}>{fmtPct(t.occupancy)}</dd></div>
        <div style={s.fact}><dt style={s.dt}>Handled per hour</dt><dd style={s.dd}>{handledPerHour == null ? '—' : handledPerHour.toFixed(1)}</dd></div>
        <div style={s.fact}><dt style={s.dt}>Logged in</dt><dd style={s.dd}>{fmtHm(t.loginSecs)}</dd></div>
      </dl>
      {t.unnamedSecs > 0 && (
        <p style={{ ...s.note, color: c.warningText }}>
          {fmtHm(t.unnamedSecs)} of this agent&rsquo;s time is under presence codes Genesys does not yet show the
          portal, so Efficiency cannot be worked out. It appears once the portal&rsquo;s Genesys access covers
          every division.
        </p>
      )}
      <table style={s.table}>
        <tbody>
          <tr><th colSpan={2} style={s.th}>Working · {fmtHm(t.workingSecs)}</th></tr>
          {rows(t.breakdown.working, WORKING_LABELS)}
          <tr><th colSpan={2} style={s.th}>Not counted: break and lunch</th></tr>
          {rows(t.breakdown.subtracted, { break: 'Break', lunch: 'Lunch' })}
          <tr><th colSpan={2} style={s.th}>Counted against Efficiency</th></tr>
          {rows(t.breakdown.other)}
        </tbody>
      </table>
      <p style={s.note}>
        Efficiency = working time ÷ (logged in − break − lunch) = {fmtHm(t.workingSecs)} ÷ {fmtHm(t.denominatorSecs)}.
        Occupancy is time on interactions over time routed On Queue.
      </p>
    </>
  );
}

const s = {
  scrim:   { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.25)', zIndex: 40 },
  panel:   { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(480px, 100vw)', background: c.surface, zIndex: 41, overflowY: 'auto', padding: '18px 20px', boxShadow: '-8px 0 24px rgba(15, 23, 42, 0.12)', display: 'flex', flexDirection: 'column', gap: '10px' },
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' },
  eyebrow: { fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: c.textMuted },
  h2:      { fontSize: '18px', fontWeight: 650, color: c.text, margin: '2px 0 0' },
  sub:     { fontSize: '12px', color: c.textMuted, marginTop: '2px' },
  h3:      { fontSize: '12px', fontWeight: 650, color: c.text, margin: '8px 0 0' },
  facts:   { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', margin: 0 },
  fact:    { background: c.surfaceSunk, borderRadius: '8px', padding: '8px 10px' },
  dt:      { fontSize: '10.5px', color: c.textMuted },
  dd:      { margin: '2px 0 0', fontSize: '16px', fontWeight: 600, color: c.textStrong, fontVariantNumeric: 'tabular-nums' },
  note:    { fontSize: '11px', color: c.textMuted, margin: 0, lineHeight: 1.45 },
  muted:   { fontSize: '12px', color: c.textMuted },
  error:   { fontSize: '12px', color: c.dangerText },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:      { ...tbl.thSm, textAlign: 'left' },
  thNum:   { ...tbl.thRSm },
  td:      { ...tbl.tdSm, color: c.textStrong },
  num:     { ...tbl.tdRSm, color: c.textStrong },
};
